import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect } from "react";
import { TextInput, useGetList } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { Box, FormControl, FormHelperText, InputLabel, MenuItem, Select, Typography, } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EMobileCandidateLists } from "@sequentech/ui-core";
import { SLATES_FORM_FIELD, checkSlatesConfiguration, checkSlatesStructure, formatSlatesProblems, getMobileCandidateLists, setMobileCandidateLists, } from "@/utils/slates";
var MOBILE_LISTS_LABEL_ID = "slates-mobile-candidate-lists-label";
export var SlatesConfigurationInput = function (_a) {
    var defaultLanguage = _a.defaultLanguage, contests = _a.contests, tenantId = _a.tenantId, electionEventId = _a.electionEventId;
    var t = useTranslation().t;
    var _b = useFormContext(), setValue = _b.setValue, trigger = _b.trigger, getFieldState = _b.getFieldState;
    var text = useWatch({ name: SLATES_FORM_FIELD });
    var mobileCandidateLists = getMobileCandidateLists(text);
    var hasConfiguration = !!(text === null || text === void 0 ? void 0 : text.trim());
    var _c = useGetList("sequent_backend_candidate", {
        filter: { tenant_id: tenantId, election_event_id: electionEventId },
        pagination: { page: 1, perPage: 9999 },
    }, { enabled: hasConfiguration && !!tenantId && !!electionEventId }), candidates = _c.data, candidatesTotal = _c.total;
    var isCandidateListPartial = !!candidates && (candidatesTotal !== null && candidatesTotal !== void 0 ? candidatesTotal : 0) > candidates.length;
    useEffect(function () {
        if (candidates && getFieldState(SLATES_FORM_FIELD).isDirty) {
            trigger(SLATES_FORM_FIELD);
        }
    }, [candidates, getFieldState, trigger]);
    var validate = useCallback(function (value) {
        if (!(value === null || value === void 0 ? void 0 : value.trim())) {
            return undefined;
        }
        if (!contests || !candidates) {
            return t("electionScreen.slates.loading");
        }
        try {
            var problems = isCandidateListPartial
                ? checkSlatesStructure(value)
                : checkSlatesConfiguration(value, defaultLanguage, contests, candidates);
            return problems.length > 0 ? formatSlatesProblems(problems) : undefined;
        }
        catch (error) {
            return error instanceof Error ? error.message : String(error);
        }
    }, [contests, candidates, isCandidateListPartial, defaultLanguage, t]);
    var onMobileCandidateListsChange = function (event) {
        var selected = Object.values(EMobileCandidateLists).find(function (option) { return option === event.target.value; });
        if (!selected || !text) {
            return;
        }
        setValue(SLATES_FORM_FIELD, setMobileCandidateLists(text, selected), {
            shouldDirty: true,
            shouldValidate: true,
        });
    };
    return (_jsxs(Box, { sx: { width: "100%", marginBottom: "1rem" }, children: [_jsx(Typography, { variant: "body1", component: "span", sx: { fontWeight: "bold" }, children: t("electionScreen.slates.title") }), _jsx(TextInput, { source: SLATES_FORM_FIELD, label: String(t("electionScreen.slates.configuration")), helperText: String(t("electionScreen.slates.helper")), validate: validate, multiline: true, minRows: 6, maxRows: 24, fullWidth: true, slotProps: {
                    htmlInput: { spellCheck: false, style: { fontFamily: "monospace" } },
                    formHelperText: { sx: { whiteSpace: "pre-wrap" } },
                } }), _jsxs(FormControl, { fullWidth: true, disabled: !mobileCandidateLists, children: [_jsx(InputLabel, { id: MOBILE_LISTS_LABEL_ID, children: t("electionScreen.slates.mobileCandidateLists.label") }), _jsx(Select, { labelId: MOBILE_LISTS_LABEL_ID, label: t("electionScreen.slates.mobileCandidateLists.label"), value: mobileCandidateLists !== null && mobileCandidateLists !== void 0 ? mobileCandidateLists : "", onChange: onMobileCandidateListsChange, children: Object.values(EMobileCandidateLists).map(function (option) { return (_jsx(MenuItem, { value: option, children: t("electionScreen.slates.mobileCandidateLists.options.".concat(option)) }, option)); }) }), _jsx(FormHelperText, { disabled: false, children: t("electionScreen.slates.mobileCandidateLists.helper") })] })] }));
};
